import { ChevronsUpDown, Building2 } from "lucide-react";
import { useAuth, type OrgMembership } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Admin",
  OPERADOR: "Operador",
  VISOR: "Visor",
};

const ROLE_COLORS: Record<string, string> = {
  ADMIN: "bg-primary/15 text-primary border-primary/30",
  OPERADOR: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  VISOR: "bg-muted text-muted-foreground border-border",
};

interface Props {
  collapsed?: boolean;
}

export function OrganizationSwitcher({ collapsed = false }: Props) {
  const { orgId, userRole, memberships, switchOrg } = useAuth();

  const activeOrg = memberships.find((m) => m.org_id === orgId);
  const rolLabel = userRole ? ROLE_LABELS[userRole] ?? userRole : null;
  const rolColor = userRole ? ROLE_COLORS[userRole] ?? ROLE_COLORS.VISOR : "";
  const hasMultiple = memberships.length > 1;

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        {rolLabel && (
          <Badge variant="outline" className={`text-[9px] h-4 px-1 font-semibold ${rolColor}`}>
            {rolLabel.charAt(0)}
          </Badge>
        )}
      </div>
    );
  }

  if (!hasMultiple) {
    return (
      <div className="flex items-center gap-2 px-1 py-1.5 rounded-md bg-muted/50 border border-border">
        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium text-foreground truncate flex-1">
          {activeOrg?.org_name ?? "Sin organización"}
        </span>
        {rolLabel && (
          <Badge variant="outline" className={`text-[9px] h-4 px-1.5 font-semibold ${rolColor}`}>
            {rolLabel}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-1 py-1.5 rounded-md bg-muted/50 border border-border w-full hover:bg-muted/80 transition-colors text-left">
          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-xs font-medium text-foreground truncate flex-1">
            {activeOrg?.org_name ?? "Sin organización"}
          </span>
          {rolLabel && (
            <Badge variant="outline" className={`text-[9px] h-4 px-1.5 font-semibold shrink-0 ${rolColor}`}>
              {rolLabel}
            </Badge>
          )}
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[220px]">
        {memberships.map((m) => (
          <DropdownMenuItem
            key={m.org_id}
            onClick={() => switchOrg(m.org_id)}
            className={m.org_id === orgId ? "bg-accent font-medium" : ""}
          >
            <div className="flex items-center gap-2 w-full min-w-0">
              <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-xs truncate flex-1">{m.org_name}</span>
              <Badge
                variant="outline"
                className={`text-[9px] h-4 px-1 font-semibold shrink-0 ${ROLE_COLORS[m.rol] ?? ROLE_COLORS.VISOR}`}
              >
                {ROLE_LABELS[m.rol] ?? m.rol}
              </Badge>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
